import React, {useEffect, useMemo, useState} from "react";
import {doc, onSnapshot} from "firebase/firestore";
import {db} from "../../Security/firebase-config";
import {useTeam} from "../../context/TeamContext";
import {
  endLiveLobby,
  getLiveLobbyShutdownLabel,
  heartbeatLiveLobby,
  leaveLiveLobby,
} from "../../utils/liveLobby";
import NoteInputBox from "./NoteInputBox";
import NotesDisplay from "./NotesDisplay";
import SegmentPlaylistVideo from "./SegmentPlaylistVideo";
import "./LiveLobbyRoomModal.css";

function toMillis(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

function formatCountdown(seconds) {
  const total = Math.max(0, Math.ceil(Number(seconds || 0)));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

function formatMatchClock(seconds) {
  const total = Math.max(0, Number(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = Math.floor(total % 60);
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function LiveLobbyRoomModal({
  session,
  hostMode = false,
  onClose,
  onLobbyFinalized,
}) {
  const {teamId, username} = useTeam();
  const [lobby, setLobby] = useState(session?.lobby || null);
  const [error, setError] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [noteTimeOverrideSec, setNoteTimeOverrideSec] = useState(null);
  const [noteTimeInput, setNoteTimeInput] = useState("");
  const [endingLobby, setEndingLobby] = useState(false);
  const [segmentCurrentTime, setSegmentCurrentTime] = useState(0);

  const lobbyId = session?.lobby?.id || session?.lobbyId || null;

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!teamId || !lobbyId) return undefined;

    return onSnapshot(doc(db, "teams", teamId, "liveLobbies", lobbyId), (snap) => {
      if (!snap.exists()) return;
      const next = {id: snap.id, ...snap.data()};
      setLobby(next);

      if (String(next?.status || "") === "finalized" && typeof onLobbyFinalized === "function") {
        onLobbyFinalized(next);
      }
    });
  }, [lobbyId, onLobbyFinalized, teamId]);

  const startedAtMs = useMemo(() => {
    return toMillis(lobby?.startedAt) || Date.now();
  }, [lobby?.startedAt]);

  useEffect(() => {
    if (!teamId || !lobbyId) return undefined;

    let cancelled = false;
    const pingLobby = async () => {
      try {
        const response = await heartbeatLiveLobby({
          teamId,
          lobbyId,
          elapsedSec: hostMode ? Math.max(0, Number((nowMs - startedAtMs) / 1000)) : 0,
        });

        if (!cancelled && response?.lobby) {
          setLobby((current) => ({...(current || {}), ...response.lobby}));
        }
      } catch (heartbeatError) {
        if (!cancelled) {
          console.warn("[LiveLobbyRoomModal] Heartbeat failed:", heartbeatError);
        }
      }
    };

    pingLobby();
    const interval = window.setInterval(pingLobby, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [hostMode, lobbyId, nowMs, startedAtMs, teamId]);

  useEffect(() => {
    return () => {
      if (!hostMode && teamId && lobbyId) {
        leaveLiveLobby({teamId, lobbyId}).catch((leaveError) => {
          console.warn("[LiveLobbyRoomModal] Failed to leave live lobby during cleanup:", leaveError);
        });
      }
    };
  }, [hostMode, lobbyId, teamId]);

  const currentLiveSec = useMemo(() => {
    return Math.max(0, (nowMs - startedAtMs) / 1000);
  }, [nowMs, startedAtMs]);
  const currentObservedSec = hostMode ?
    currentLiveSec :
    Math.max(0, Number(segmentCurrentTime || 0));

  const currentMatchTimeSec =
    noteTimeOverrideSec === null ?
      Number(currentObservedSec.toFixed(1)) :
      noteTimeOverrideSec;

  const shutdownAtMs = toMillis(lobby?.shutdownAt);
  const shutdownCountdownSec =
    shutdownAtMs && shutdownAtMs > nowMs ?
      Math.max(0, Math.ceil((shutdownAtMs - nowMs) / 1000)) :
      0;

  const playbackDelaySeconds = Math.round(
    Math.max(0, Number(lobby?.playbackDelayMs || 12000)) / 1000,
  );

  const freezeNoteTime = () => {
    const next = Number(currentObservedSec.toFixed(1));
    setNoteTimeOverrideSec(next);
    setNoteTimeInput(formatMatchClock(next));
  };

  const goLiveNoteTime = () => {
    setNoteTimeOverrideSec(null);
    setNoteTimeInput("");
  };

  async function handleEndLobby(reason = "manual_end") {
    if (!hostMode || !teamId || !lobbyId || endingLobby) return;

    try {
      setEndingLobby(true);
      await endLiveLobby({teamId, lobbyId, reason});
    } catch (endError) {
      console.error("[LiveLobbyRoomModal] Failed to end lobby:", endError);
      setError(endError?.message || "Unable to finalize the live lobby right now.");
    } finally {
      setEndingLobby(false);
    }
  }

  async function handleClose() {
    try {
      if (hostMode && lobby && !["finalized", "finalizing", "ended", "failed"].includes(String(lobby.status || ""))) {
        await handleEndLobby("manual_end");
      } else if (!hostMode && teamId && lobbyId) {
        await leaveLiveLobby({teamId, lobbyId});
      }
    } catch (closeError) {
      console.warn("[LiveLobbyRoomModal] Failed to close cleanly:", closeError);
    }

    onClose?.();
  }

  return (
    <div className="live-lobby-room-modal">
      <div className="live-lobby-room-modal__backdrop" onClick={handleClose} />

      <div className="live-lobby-room-modal__dialog">
        <div className="live-lobby-room-modal__header">
          <div>
            <p className="live-lobby-room-modal__eyebrow">
              {hostMode ? "Hosting Live Lobby" : "Joined Live Lobby"}
            </p>
            <h3>{lobby?.title || "Live Lobby"}</h3>
            <p className="live-lobby-room-modal__subhead">
              {(lobby?.mapName || session?.lobby?.mapName || "Map")} • {hostMode ? "You are broadcasting in near-live mode." : `Watching with about ${playbackDelaySeconds}s of live delay.`}
            </p>
          </div>

          <div className="live-lobby-room-modal__header-actions">
            <span className={`live-lobby-room-modal__status live-lobby-room-modal__status--${String(lobby?.status || "starting")}`}>
              {String(lobby?.status || "starting")}
            </span>
            {hostMode ? (
              <button
                type="button"
                className="live-lobby-room-modal__danger"
                onClick={() => handleEndLobby("manual_end")}
                disabled={endingLobby}
              >
                {endingLobby ? "Finalizing..." : "End Live Lobby"}
              </button>
            ) : null}
            <button
              type="button"
              className="live-lobby-room-modal__close app-cancel-control"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        </div>

        {error ? <div className="live-lobby-room-modal__error">{error}</div> : null}

        {lobby?.status === "quota_shutdown_grace" ? (
          <div className="live-lobby-room-modal__warning">
            <strong>{getLiveLobbyShutdownLabel(lobby?.quotaShutdownReason)}</strong>
            <span>
              The live lobby will self-terminate in {formatCountdown(shutdownCountdownSec)}. Those final 30 seconds will still be saved into the finished VOD.
            </span>
          </div>
        ) : null}

        {lobby?.status === "finalizing" ? (
          <div className="live-lobby-room-modal__finalizing">
            {lobby?.processingMessage || "Finalizing live lobby recording..."}
          </div>
        ) : null}

        <div className="live-lobby-room-modal__body">
          <div className="live-lobby-room-modal__player-card">
            <div className="live-lobby-room-modal__player-head">
              <div>
                <p>Live Feed</p>
                <span>
                  Match clock {formatMatchClock(currentLiveSec)} • delayed by about {playbackDelaySeconds}s • {Number(lobby?.activeParticipantCount || 0)} participant(s)
                </span>
              </div>
            </div>

            <div className="live-lobby-room-modal__video-shell">
              <SegmentPlaylistVideo
                teamId={teamId}
                mapId={lobby?.mapId || session?.lobby?.mapId}
                videoId={lobby?.videoId}
                lobbyId={lobby?.id || lobbyId}
                liveMode={true}
                lobbyStatus={lobby?.status}
                playbackDelayMs={Number(lobby?.playbackDelayMs || 12000)}
                muted={false}
                autoPlay={true}
                className="live-lobby-room-modal__video-player"
                onCurrentTimeChange={setSegmentCurrentTime}
                onPlaybackBlocked={(message) => {
                  setError(message);
                }}
              />
            </div>
          </div>

          <div className="live-lobby-room-modal__notes-card">
            <div className="live-lobby-room-modal__notes-head">
              <div>
                <p>Shared Live Notes</p>
                <span>Anyone on this team with VOD review access can see these notes live.</span>
              </div>

              <div className="live-lobby-room-modal__note-time">
                <label>Note time</label>
                <input
                  type="text"
                  value={noteTimeInput}
                  placeholder={noteTimeOverrideSec === null ? "LIVE" : ""}
                  onFocus={() => {
                    if (noteTimeOverrideSec === null) freezeNoteTime();
                  }}
                  onChange={(event) => {
                    const next = event.target.value;
                    setNoteTimeInput(next);
                    if (noteTimeOverrideSec === null) {
                      setNoteTimeOverrideSec(Number(currentObservedSec.toFixed(1)));
                    }

                    const parts = next.split(":").map((value) => Number(value));
                    if (parts.every((value) => Number.isFinite(value))) {
                      const [hours = 0, minutes = 0, seconds = 0] = parts;
                      setNoteTimeOverrideSec(hours * 3600 + minutes * 60 + seconds);
                    }
                  }}
                />
                <button type="button" onClick={freezeNoteTime}>Freeze</button>
                <button type="button" onClick={goLiveNoteTime}>Live</button>
              </div>
            </div>

            {teamId && lobby?.matchId && ["starting", "live", "quota_shutdown_grace", "finalizing", "finalized"].includes(String(lobby?.status || "")) ? (
              <>
                <div className="live-lobby-room-modal__note-input">
                  <NoteInputBox
                    mapId={lobby.mapId || session?.lobby?.mapId}
                    matchId={lobby.matchId}
                    matchTimeSec={currentMatchTimeSec}
                    onHide={() => {}}
                    onBeginTyping={() => {
                      if (noteTimeOverrideSec === null) {
                        freezeNoteTime();
                      }
                    }}
                    onPosted={() => {
                      goLiveNoteTime();
                    }}
                  />
                </div>

                <div className="live-lobby-room-modal__note-list">
                  <NotesDisplay
                    mapId={lobby.mapId || session?.lobby?.mapId}
                    matchId={lobby.matchId}
                    syncOffsetSec={0}
                    currentTime={segmentCurrentTime}
                    onSeek={() => {}}
                    displayMode="list"
                    isPlaying={true}
                    autoScroll={false}
                    onDisableAuto={() => {}}
                    filters={{
                      filterMentions: [],
                      includeTeam: false,
                      filterImportance: [],
                      filterTags: [],
                      hideReviewedByMe: false,
                      currentUser: username,
                    }}
                    autoScrollToNewest={true}
                  />
                </div>
              </>
            ) : (
              <div className="live-lobby-room-modal__pending">
                Live note tools are preparing...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
